import { Injectable, TemplateRef, Type } from '@angular/core';
import { MatDialog, MatDialogConfig, MatDialogRef } from '@angular/material/dialog';
import { checkIsWebView } from '../../../../helpers/screen-utils';

export interface AdaptiveDialogConfig<D = any> {
  /** Data to pass to the dialog component (injected via MAT_DIALOG_DATA) */
  data?: D;
  /** Width on desktop (default: '500px') */
  width?: string;
  /** Whether clicking outside closes the dialog (default: false) */
  disableClose?: boolean;
  /** Additional panel classes */
  panelClass?: string | string[];
  /** Auto-focus first focusable element (default: true) */
  autoFocus?: boolean;
}

export interface AdaptiveDialogRef<T, R = any> {
  /** Reference to the Material Dialog */
  dialogRef: MatDialogRef<T, R>;
  /** Close the dialog with an optional result */
  close: (result?: R) => void;
  /** Promise that resolves when the dialog is closed */
  afterClosed: () => Promise<R | undefined>;
  /** Whether this opened as a bottom sheet (mobile) */
  isBottomSheet: boolean;
  /** Whether this opened as a centered dialog (desktop) */
  isDialog: boolean;
}

// Legacy interface for backward compatibility
export interface AdaptiveConfig<D = any> extends AdaptiveDialogConfig<D> {
  modalClass?: string;
  backdrop?: boolean | 'static';
  keyboard?: boolean;
  ignoreBackdropClick?: boolean;
  title?: string;
  showCloseButton?: boolean;
  height?: string;
  maxHeight?: string;
  hasBackdrop?: boolean;
}

// Legacy interface for backward compatibility
export interface AdaptiveRef<T = any> {
  componentRef: T;
  close: (result?: any) => void;
  afterClosed: () => Promise<any>;
  isModal: boolean;
  isBottomSheet: boolean;
}

/**
 * Service for opening dialogs that automatically adapt to screen size:
 * - Desktop: Centered Material Dialog
 * - Mobile: Bottom sheet style Material Dialog
 *
 * Usage:
 * ```typescript
 * const ref = this.adaptiveService.open(MyDialogComponent, {
 *   data: { title: 'Hello' },
 *   width: '600px'
 * });
 *
 * ref.afterClosed().then(result => {
 *   console.log('Dialog closed with:', result);
 * });
 * ```
 */
@Injectable({
  providedIn: 'root'
})
export class AdaptiveContainerService {
  private readonly MOBILE_BREAKPOINT = 768;

  constructor(private dialog: MatDialog) {}

  /**
   * Opens a dialog that adapts to screen size.
   * Desktop: centered dialog
   * Mobile: bottom sheet style
   */
  open<T, D = any, R = any>(
    component: Type<T>,
    config: AdaptiveDialogConfig<D> = {}
  ): AdaptiveDialogRef<T, R> {
    const isMobile = this.isMobile();
    const desktopWidth = config.width ?? '500px';

    // Build panel classes array
    const panelClasses: string[] = [];
    if (isMobile) {
      panelClasses.push('bottom-sheet-dialog');
    }
    if (config.panelClass) {
      if (Array.isArray(config.panelClass)) {
        panelClasses.push(...config.panelClass);
      } else {
        panelClasses.push(config.panelClass);
      }
    }

    const dialogConfig: MatDialogConfig<D> = {
      data: config.data,
      width: isMobile ? '100vw' : desktopWidth,
      maxWidth: isMobile ? '100vw' : '90vw',
      maxHeight: isMobile ? '90dvh' : '90vh',
      position: isMobile ? { bottom: '0' } : undefined,
      panelClass: panelClasses.length > 0 ? panelClasses : undefined,
      disableClose: config.disableClose ?? false,
      autoFocus: config.autoFocus ?? true,
    };

    const dialogRef = this.dialog.open<T, D, R>(component, dialogConfig);

    return {
      dialogRef,
      close: (result?: R) => dialogRef.close(result),
      afterClosed: () => dialogRef.afterClosed().toPromise() as Promise<R | undefined>,
      isBottomSheet: isMobile,
      isDialog: !isMobile,
    };
  }

  /**
   * Legacy method - use open() instead.
   * Opens a component in either a dialog (desktop) or bottom sheet (mobile) based on screen size
   * @deprecated Use open() instead
   */
  openAdaptive<T, D = any, R = any>(
    component: Type<T>,
    config: AdaptiveConfig<D> = {}
  ): AdaptiveRef<T> {
    const ref = this.open<T, D, R>(component, {
      data: config.data,
      width: config.width ?? (config.modalClass === 'modal-lg' ? '800px' : '500px'),
      disableClose: config.disableClose ?? config.ignoreBackdropClick,
      panelClass: config.panelClass,
    });

    // Return legacy-compatible reference
    return {
      componentRef: ref.dialogRef.componentInstance,
      close: ref.close,
      afterClosed: ref.afterClosed,
      isModal: ref.isDialog,
      isBottomSheet: ref.isBottomSheet,
    };
  }

  /**
   * Opens a dialog and returns a promise that resolves when closed.
   * Convenience method for simple confirmation flows.
   */
  async openAsync<T, D = any, R = any>(
    component: Type<T>,
    config: AdaptiveDialogConfig<D> = {}
  ): Promise<R | undefined> {
    const ref = this.open<T, D, R>(component, config);
    return ref.afterClosed();
  }

  /**
   * Force open as centered dialog regardless of screen size
   */
  openAsDialog<T, D = any, R = any>(
    component: Type<T>,
    config: AdaptiveDialogConfig<D> = {}
  ): AdaptiveDialogRef<T, R> {
    const desktopWidth = config.width ?? '500px';

    const panelClasses: string[] = [];
    if (config.panelClass) {
      if (Array.isArray(config.panelClass)) {
        panelClasses.push(...config.panelClass);
      } else {
        panelClasses.push(config.panelClass);
      }
    }

    const dialogConfig: MatDialogConfig<D> = {
      data: config.data,
      width: desktopWidth,
      maxWidth: '90vw',
      maxHeight: '90vh',
      disableClose: config.disableClose ?? false,
      autoFocus: config.autoFocus ?? true,
      panelClass: panelClasses.length > 0 ? panelClasses : undefined,
    };

    const dialogRef = this.dialog.open<T, D, R>(component, dialogConfig);

    return {
      dialogRef,
      close: (result?: R) => dialogRef.close(result),
      afterClosed: () => dialogRef.afterClosed().toPromise() as Promise<R | undefined>,
      isBottomSheet: false,
      isDialog: true,
    };
  }

  /**
   * Legacy method - use openAsDialog() instead.
   * @deprecated Use openAsDialog() instead
   */
  openAsModal<T, D = any, R = any>(
    component: Type<T>,
    config: AdaptiveConfig<D> = {}
  ): AdaptiveRef<T> {
    const ref = this.openAsDialog<T, D, R>(component, {
      data: config.data,
      width: config.width ?? (config.modalClass === 'modal-lg' ? '800px' : '500px'),
      disableClose: config.disableClose ?? config.ignoreBackdropClick,
      panelClass: config.panelClass,
    });

    return {
      componentRef: ref.dialogRef.componentInstance,
      close: ref.close,
      afterClosed: ref.afterClosed,
      isModal: true,
      isBottomSheet: false,
    };
  }

  /**
   * Force open as bottom sheet regardless of screen size
   */
  openAsBottomSheet<T, D = any, R = any>(
    component: Type<T>,
    config: AdaptiveDialogConfig<D> = {}
  ): AdaptiveDialogRef<T, R> {
    const panelClasses: string[] = ['bottom-sheet-dialog'];
    if (config.panelClass) {
      if (Array.isArray(config.panelClass)) {
        panelClasses.push(...config.panelClass);
      } else {
        panelClasses.push(config.panelClass);
      }
    }

    const dialogConfig: MatDialogConfig<D> = {
      data: config.data,
      width: '100vw',
      maxWidth: '100vw',
      maxHeight: '90dvh',
      position: { bottom: '0' },
      panelClass: panelClasses,
      disableClose: config.disableClose ?? false,
      autoFocus: config.autoFocus ?? true,
    };

    const dialogRef = this.dialog.open<T, D, R>(component, dialogConfig);

    return {
      dialogRef,
      close: (result?: R) => dialogRef.close(result),
      afterClosed: () => dialogRef.afterClosed().toPromise() as Promise<R | undefined>,
      isBottomSheet: true,
      isDialog: false,
    };
  }

  /**
   * Opens a template that adapts to screen size.
   * Desktop: centered dialog
   * Mobile: bottom sheet style
   *
   * Usage:
   * ```typescript
   * @ViewChild('myTemplate') myTemplate: TemplateRef<any>;
   *
   * openSheet() {
   *   const ref = this.adaptiveService.openTemplate(this.myTemplate);
   *   ref.afterClosed().then(() => console.log('closed'));
   * }
   * ```
   */
  openTemplate<D = any, R = any>(
    template: TemplateRef<any>,
    config: AdaptiveDialogConfig<D> = {}
  ): AdaptiveDialogRef<any, R> {
    const isMobile = this.isMobile();
    const desktopWidth = config.width ?? '500px';

    const panelClasses: string[] = [];
    if (isMobile) {
      panelClasses.push('bottom-sheet-dialog');
    }
    if (config.panelClass) {
      if (Array.isArray(config.panelClass)) {
        panelClasses.push(...config.panelClass);
      } else {
        panelClasses.push(config.panelClass);
      }
    }

    const dialogConfig: MatDialogConfig<D> = {
      data: config.data,
      width: isMobile ? '100vw' : desktopWidth,
      maxWidth: isMobile ? '100vw' : '90vw',
      maxHeight: isMobile ? '90dvh' : '90vh',
      position: isMobile ? { bottom: '0' } : undefined,
      panelClass: panelClasses.length > 0 ? panelClasses : undefined,
      disableClose: config.disableClose ?? false,
      autoFocus: config.autoFocus ?? true,
    };

    const dialogRef = this.dialog.open<any, D, R>(template, dialogConfig);

    return {
      dialogRef,
      close: (result?: R) => dialogRef.close(result),
      afterClosed: () => dialogRef.afterClosed().toPromise() as Promise<R | undefined>,
      isBottomSheet: isMobile,
      isDialog: !isMobile,
    };
  }

  /**
   * Check if current viewport is mobile-sized
   */
  isMobile(): boolean {
    // Use checkIsWebView for consistency with existing code
    // Returns true if NOT web view (i.e., mobile native or small screen)
    return !checkIsWebView() || window.innerWidth <= this.MOBILE_BREAKPOINT;
  }

  /**
   * Close all open dialogs
   */
  closeAll(): void {
    this.dialog.closeAll();
  }
}
